import { useContext } from 'react';

import { useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import ResumeShareModal from 'src/components/HiringManager/ResumeShareModal';
import PfpBillingButton from 'src/components/PfpBillingButton';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';
import { useIsZarplataPlatform } from 'src/hooks/usePlatform';
import { useSelector } from 'src/hooks/useSelector';
import { Permission } from 'src/models/resume/resumeCommon.types';
import { UserType } from 'src/models/userType';
import { VIEW_WITHOUT_CONTACTS } from 'src/utils/constants/resumePermission';

import ActionsWrapper from 'src/components/ResumeCard/Actions/ActionsWrapper';
import AdditionalMenuButton from 'src/components/ResumeCard/Actions/Additional';
import CallButton from 'src/components/ResumeCard/Actions/CallButton/Wrapper';
import ChatikContainer from 'src/components/ResumeCard/Actions/Chatik';
import ChatikButton from 'src/components/ResumeCard/Actions/Chatik/Button/Wrapper';
import CommentsContainer from 'src/components/ResumeCard/Actions/Comments';
import CommentsButton from 'src/components/ResumeCard/Actions/Comments/Button/Wrapper';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import DiscardButton from 'src/components/ResumeCard/Actions/DiscardButton';
import InviteButton from 'src/components/ResumeCard/Actions/InviteButton/Wrapper';
import OpenContacts from 'src/components/ResumeCard/Actions/OpenContacts';
import RemoveFromSuitableButton from 'src/components/ResumeCard/Actions/RemoveFromSuitableButton/Wrapper';
import SaveToFavoritesButton from 'src/components/ResumeCard/Actions/SaveToFavoritesButton';
import SelectCardButton from 'src/components/ResumeCard/Actions/SelectCardButton';
import ShareButton from 'src/components/ResumeCard/Actions/ShareButton';

import styles from './index.less';

const Features = {
    hideInterfaceForModerators: 'hide_interface_for_moderators',
};

const TrlKeys = {
    emptyResume: 'resume.title.empty',
};

const ActionsUniversal: TranslatedComponent = ({ trls }) => {
    const isHiringManagerExp = useSelector((state) => state.isHiringManagerExperiment);
    const [isShareResumeModalVisible, showShareResumeModal, hideShareResumeModal] = useOnOffState(false);
    const additionalCheck = useSelector(({ additionalCheck }) => additionalCheck);
    const { resume, isSelected, select, isFoldersPage, isReadOnly, onResumeUpdate, isFirstCard } =
        useContext(ResumeCardActionsContext);

    const hideInterfaceForModerators = useSelector(
        (state) => state.features[Features.hideInterfaceForModerators] as string
    );
    const userId = useSelector((state) => state.userId);
    const hideForModerators = hideInterfaceForModerators?.split(',')?.includes(userId);
    const showFullActions = resume._attributes?.permission !== VIEW_WITHOUT_CONTACTS && !hideForModerators;

    const isZp = useIsZarplataPlatform();
    const showAdditionalMenuButton = !isZp && showFullActions;

    const { isMobile } = useBreakpoint();

    const isEmployer = useSelector(({ userType }) => UserType.Employer === userType);
    if (!isEmployer) {
        return null;
    }

    if (additionalCheck) {
        return null;
    }

    const pfpBillingButton =
        resume.canOpenContacts &&
        resume.hasPfpTopics &&
        resume._attributes.permission === Permission.ViewWithoutContacts ? (
            <PfpBillingButton
                resumeHash={resume._attributes.hash}
                topicId={resume.topicId}
                resumeId={resume._attributes.id}
                onContactsBought={onResumeUpdate}
            />
        ) : null;

    const getMobileActions = () => (
        <div className={styles.mobile}>
            {isFoldersPage && (
                <div className={styles.mobileRow}>
                    <SelectCardButton isSelected={isSelected} select={select} />
                    <div className={styles.stretched}>
                        {showFullActions ? <InviteButton isMobile /> : <SaveToFavoritesButton isMobile />}
                    </div>
                </div>
            )}
            {showFullActions && (
                <>
                    {!isFoldersPage && <InviteButton isMobile />}
                    <ChatikButton isMobile />
                </>
            )}
            {pfpBillingButton}
            {!pfpBillingButton && <OpenContacts />}
            <DiscardButton isMobile />
            <CommentsButton isMobile />
            <RemoveFromSuitableButton isMobile />
            {isHiringManagerExp && <ShareButton onClick={showShareResumeModal} showInfotip={!!isFirstCard} />}
            {(showFullActions || !isFoldersPage) && (
                <div className={styles.mobileRow}>
                    <div className={styles.stretched}>
                        <SaveToFavoritesButton isMobile />
                    </div>
                    {showAdditionalMenuButton && <AdditionalMenuButton />}
                </div>
            )}
        </div>
    );

    const getDesktopActions = () => (
        <div className={styles.desktop}>
            {isFoldersPage && <SelectCardButton isSelected={isSelected} select={select} />}
            {showFullActions && (
                <>
                    <InviteButton />
                    <CallButton />
                    <ChatikButton />
                </>
            )}
            {pfpBillingButton}
            {!pfpBillingButton && <OpenContacts />}
            {!isReadOnly && <DiscardButton />}
            <CommentsButton />
            <div className={styles.desktopDivider} />
            <RemoveFromSuitableButton />
            {isHiringManagerExp && <ShareButton onClick={showShareResumeModal} showInfotip={!!isFirstCard} />}
            <SaveToFavoritesButton />
            {showAdditionalMenuButton && <AdditionalMenuButton />}
        </div>
    );

    return (
        <ActionsWrapper>
            <VSpacing default={12} />
            {isMobile ? getMobileActions() : getDesktopActions()}
            <CommentsContainer />
            <ChatikContainer />
            {isHiringManagerExp && (
                <ResumeShareModal
                    resumeHash={[resume._attributes.hash]}
                    resumeName={[resume.title[0]?.string || trls[TrlKeys.emptyResume]]}
                    visible={isShareResumeModalVisible}
                    setOff={hideShareResumeModal}
                    topicIds={[resume.topicId].filter(Boolean) as string[]}
                />
            )}
        </ActionsWrapper>
    );
};

export default translation(ActionsUniversal);
